/**
 * Product Engraving Upsell Custom Element
 * Handles the engraving upsell functionality with modal and cart integration.
 *
 * Required external CSS:
 *   - assets/engraving-notification.css (notification toast styles)
 *
 * Required globals:
 *   - window.routes.cart_add_url: Shopify cart add API endpoint
 *   - window.updateCart (optional): Function to refresh cart drawer UI after adding items
 *
 * Data attributes on host element:
 *   - data-product-handle: Handle of the engraving product (default: 'gravur')
 *   - data-engraving-price: Display price string (e.g. '9,95 €')
 *   - data-max-chars: Maximum characters for engraving text (default: 10)
 *   - data-modal-id: ID of the associated engraving modal element
 *
 * Liquid block: blocks/product_engraving-option.liquid
 * Modal snippet: snippets/engraving-modal.liquid
 */

if (!customElements.get('product-engraving-upsell')) {
  // Constants
  const AUTO_FOCUS_DELAY_MS = 300;
  const NOTIFICATION_DURATION_MS = 4000;

  class ProductEngravingUpsell extends HTMLElement {
    constructor() {
      super();

      this.productHandle = this.dataset.productHandle || 'gravur';
      this.maxChars = parseInt(this.dataset.maxChars) || 10;
      this.placeholder = this.dataset.placeholder || '';
      this.errorUnavailable = this.dataset.errorUnavailable || 'Engraving service could not be added. Please refresh the page.';
      this.errorAddFailed = this.dataset.errorAddFailed || 'Error adding engraving. Please try again.';
      this.cartPropertyText = this.dataset.cartPropertyText || 'Engraving';
      this.cartPropertyForProduct = this.dataset.cartPropertyForProduct || 'For Product';
      this.engravingText = '';
      this.isActive = false;
      this.engravingVariantId = null;

      this.linkBtn = this.querySelector('.engraving-link-btn');
      this.textPreview = this.querySelector('.engraving-text-preview');
      this.textContentElement = this.querySelector('.engraving-text-content');

      this.modal = document.getElementById('engravingModalOverlay');
      this.modalInput = document.getElementById('engravingInput');
      this.charCounter = document.getElementById('engravingCharCount');
      this.confirmBtn = document.getElementById('engravingModalConfirm');
      this.closeBtn = document.getElementById('engravingModalClose');

      this.init();
    }

    init() {
      // Fetch engraving product variant ID
      this.fetchEngravingProduct();

      // Link button click - always open modal
      if (this.linkBtn) {
        this.linkBtn.addEventListener('click', () => {
          this.openModal();
        });
      }

      // Modal controls
      if (this.modalInput) {
        this.modalInput.addEventListener('input', (e) => this.handleInput(e));
      }

      if (this.confirmBtn) {
        this.confirmBtn.addEventListener('click', () => this.confirmEngraving());
      }

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.closeModal());
      }

      // Click outside modal to close
      if (this.modal) {
        this.modal.addEventListener('click', (e) => {
          if (e.target === this.modal) {
            this.closeModal();
          }
        });
      }

      // ESC key to close modal
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal && this.modal.classList.contains('active')) {
          this.closeModal();
        }
      });
    }

    /**
     * Show a toast notification instead of alert()
     * @param {string} message - The message to display
     * @param {string} type - 'error' or 'success'
     */
    showNotification(message, type = 'error') {
      // Remove any existing notification
      const existingNotification = document.querySelector('.engraving-notification');
      if (existingNotification) {
        existingNotification.remove();
      }

      // Create notification element
      const notification = document.createElement('div');
      notification.className = `engraving-notification engraving-notification--${type}`;
      notification.setAttribute('role', 'alert');
      notification.innerHTML = `
        <span class="engraving-notification__message">${message}</span>
        <button class="engraving-notification__close" aria-label="Close">&times;</button>
      `;

      // Notification styles loaded from assets/engraving-notification.css

      document.body.appendChild(notification);

      // Close button functionality
      notification.querySelector('.engraving-notification__close').addEventListener('click', () => {
        notification.remove();
      });

      // Auto-remove after delay
      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove();
        }
      }, NOTIFICATION_DURATION_MS);
    }

    async fetchEngravingProduct() {
      try {
        const response = await fetch(`/products/${this.productHandle}.js`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to fetch engraving product`);
        }

        const product = await response.json();

        if (product && product.variants && product.variants.length > 0) {
          this.engravingVariantId = product.variants[0].id;
        }
      } catch (error) {
        // Silently fail - engraving option will be unavailable
      }
    }

    openModal() {
      if (!this.modal) return;

      this.modal.classList.add('active');
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = (window.innerWidth - document.documentElement.clientWidth) + 'px';

      // Reset input
      if (this.modalInput) {
        this.modalInput.value = this.engravingText;
        this.modalInput.focus();
        this.handleInput({ target: this.modalInput });
      }

      // Auto-focus Confirm button after a brief delay (when text is entered)
      setTimeout(() => {
        if (this.confirmBtn && !this.confirmBtn.disabled) {
          this.confirmBtn.focus();
        }
      }, AUTO_FOCUS_DELAY_MS);
    }

    closeModal() {
      if (!this.modal) return;

      this.modal.classList.remove('active');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';

      // If no text was entered and closing, deactivate
      if (!this.engravingText && this.isActive) {
        this.deactivate();
      }
    }

    handleInput(e) {
      const value = e.target.value;
      const length = value.length;

      // Update character counter
      if (this.charCounter) {
        this.charCounter.textContent = length;

        const counterParent = this.charCounter.parentElement;
        counterParent.classList.remove('warning', 'error');

        if (length >= this.maxChars) {
          counterParent.classList.add('error');
        } else if (length >= this.maxChars - 2) {
          counterParent.classList.add('warning');
        }
      }

      // Enable/disable confirm button
      if (this.confirmBtn) {
        this.confirmBtn.disabled = !value.trim() || length > this.maxChars;
      }

      // Validation styling
      if (this.modalInput) {
        if (length > this.maxChars) {
          this.modalInput.classList.add('error');
        } else {
          this.modalInput.classList.remove('error');
        }
      }
    }

    async confirmEngraving() {
      const text = this.modalInput ? this.modalInput.value.trim() : '';

      if (!text || text.length > this.maxChars) {
        return;
      }

      this.engravingText = text;
      this.closeModal();

      // Activate toggle and show preview
      await this.activate();
    }

    async activate() {
      if (!this.engravingVariantId) {
        this.showNotification(this.errorUnavailable, 'error');
        return;
      }

      try {
        // Add engraving product to cart with custom property
        const formData = {
          items: [{
            id: this.engravingVariantId,
            quantity: 1,
            properties: {
              [this.cartPropertyText]: this.engravingText,
              ['_' + this.cartPropertyForProduct]: document.querySelector('[name="product-id"]')?.value || ''
            }
          }]
        };

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData)
        });

        if (!response.ok) {
          throw new Error('Failed to add engraving to cart');
        }

        const result = await response.json();

        // Update UI
        this.isActive = true;
        if (this.linkBtn) {
          this.linkBtn.classList.add('active');
        }

        if (this.textPreview && this.textContentElement) {
          this.textContentElement.textContent = `"${this.engravingText}"`;
          this.textPreview.classList.add('active');
        }

        // Trigger cart update event
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { source: 'engraving-upsell' }
        }));

        // Update cart drawer if it exists
        if (typeof window.updateCart === 'function') {
          window.updateCart();
        }

      } catch (error) {
        this.showNotification(this.errorAddFailed, 'error');
        this.deactivate();
      }
    }

    async deactivate() {
      if (!this.engravingVariantId) return;

      try {
        // Get current cart
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        // Find engraving item(s) with matching text
        const engravingItems = cart.items.filter(item =>
          item.variant_id === this.engravingVariantId &&
          item.properties &&
          item.properties[this.cartPropertyText] === this.engravingText
        );

        // Remove engraving items
        for (const item of engravingItems) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: item.key,
              quantity: 0
            })
          });
        }

        // Update UI
        this.isActive = false;
        this.engravingText = '';

        if (this.linkBtn) {
          this.linkBtn.classList.remove('active');
        }

        if (this.textPreview) {
          this.textPreview.classList.remove('active');
        }

        if (this.modalInput) {
          this.modalInput.value = '';
        }

        // Trigger cart update
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { source: 'engraving-upsell' }
        }));

        if (typeof window.updateCart === 'function') {
          window.updateCart();
        }

      } catch (error) {
        // Silently fail - cart will be refreshed on next page load
      }
    }
  }

  customElements.define('product-engraving-upsell', ProductEngravingUpsell);
}

// Component is auto-initialized via customElements.define()
